import Popover, { PopoverProps } from './Popover'
import cn from 'utils/cn'
import { useCallback, useEffect, useRef, useState } from 'react'

export interface TooltipProps extends Omit<PopoverProps, 'show'> {
  /**
   * Additional class names for the tooltip container.
   */
  className?: string
  /**
   * Determines how the tooltip is triggered: on hover or click.
   */
  triggerMode?: 'hover' | 'click'
  /**
   * If true, disables the tooltip display.
   */
  disabled?: boolean
  /**
   * The duration (ms) the tooltip remains visible after mouseover. Only works in `hover` mode.
   */
  exitDelay?: number
  /**
   * Callback fired when tooltip visibility changes.
   */
  onVisibilityChange?: (visibility: boolean) => void
  /**
   * If true, dismisses the tooltip on any interaction.
   */
  dismissOnInteraction?: boolean
  /**
   * If true, dismisses the tooltip when mouse leaves the trigger element.
   */
  dismissOnMouseLeave?: boolean
  /**
   * Additional class names for the tooltip wrapper element.
   */
  wrapperClassName?: string
  /**
   * Additional class names for the tooltip content element.
   */
  contentClassName?: string
}

const Tooltip = ({
  children,
  content,
  triggerMode = 'hover',
  exitDelay = 0,
  disabled,
  onVisibilityChange,
  dismissOnInteraction = false,
  dismissOnMouseLeave = false,
  wrapperClassName,
  contentClassName,
  ...props
}: TooltipProps) => {
  const [show, setShow] = useState(false)
  const timer = useRef<NodeJS.Timeout | null>(null)
  const onHoverTrigger = useCallback(() => {
    if (triggerMode === 'hover') {
      setShow(!disabled)
      if (timer.current) {
        clearTimeout(timer.current)
      }
    }
  }, [disabled, triggerMode])
  const onHoverShutdown = useCallback(() => {
    if (triggerMode === 'hover' || dismissOnMouseLeave) {
      timer.current = setTimeout(() => {
        setShow(false)
      }, exitDelay)
    }
  }, [exitDelay, triggerMode, dismissOnMouseLeave])

  useEffect(() => {
    onVisibilityChange?.(show)
  }, [onVisibilityChange, show])

  useEffect(() => {
    const listener = () => {
      setShow(false)
    }
    document.addEventListener('click', listener)
    return () => document.removeEventListener('click', listener)
  }, [])

  return (
    <Popover
      content={
        <div
          className={cn(
            typeof content === 'string' && 'p-4 rounded-md  shadow-md',
            props?.dark ? 'bg-dark text-white p-4 rounded-md' : 'bg-white',
            contentClassName
          )}
          onMouseEnter={onHoverTrigger}
          onMouseLeave={onHoverShutdown}
          onClick={(e) => {
            e.stopPropagation()
            if (dismissOnInteraction) setShow(false)
          }}
        >
          {content}
        </div>
      }
      show={show}
      {...props}
    >
      <div
        className={cn(
          wrapperClassName ? wrapperClassName : 'inline-block leading-none'
        )}
        tabIndex={10000}
        onMouseEnter={onHoverTrigger}
        onMouseLeave={onHoverShutdown}
        onClick={(e) => {
          e.stopPropagation()
          return triggerMode === 'click' ? setShow(!disabled) : null
        }}
        onFocusCapture={() => {
          return triggerMode === 'click' ? setShow(!disabled) : null
        }}
      >
        {children}
      </div>
    </Popover>
  )
}

export default Tooltip
